import React from 'react';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormikProvider, useFormik } from 'formik';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Button, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { useParams } from 'react-router-dom';
import { Loader } from 'app/shared/loader/loader';
import { fetchRpjLocationQuery, saveRpjLocationQuery } from 'app/edozvola/cases/case-location/service/case-location.queries';
import { caseRpjLocationValidationSchema } from 'app/edozvola/cases/case-location/model/case-location.schema';
import {
  fetchCityMunicipalitiesWithCountyQuery,
  fetchCommunitiesWithCityMunicipalitiesAndCountyQuery,
  fetchHouseNumbersQuery,
  fetchStreetsQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { initialRpjLocation } from 'app/edozvola/cases/case-location/model/case-location.model';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';

export interface IRpjLocationFormProps {
  rpjLocationId: string | null;
  setRpjLocationId: any;
  readOnly: boolean;
}

const CaseLocationRpjLocationForm = (props: IRpjLocationFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const { data: rpjLocationData, isLoading: rpjLocationDataLoading } = fetchRpjLocationQuery(props.rpjLocationId);

  const formik = useFormik({
    initialValues: rpjLocationData || initialRpjLocation,
    onSubmit: values => saveRpjLocationMutation(values),
    validationSchema: caseRpjLocationValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { data: cityMunicipalitiesOptions, isLoading: cityMunicipalityOptionsLoading } = fetchCityMunicipalitiesWithCountyQuery();
  const { data: communitiesOptions, isLoading: communitiesOptionsLoading } = fetchCommunitiesWithCityMunicipalitiesAndCountyQuery();
  const { data: streetOptions, isLoading: streetOptionsLoading } = fetchStreetsQuery(formik.values?.community ? formik.values?.community[0]?.id : undefined,);
  const { data: houseNumberOptions, isLoading: houseNumberOptionsLoading } = fetchHouseNumbersQuery(formik.values?.street ? formik.values?.street[0]?.id : undefined,);

  const { mutate: saveRpjLocationMutation, isPending: isRpjLocationSaving } = saveRpjLocationQuery(uuid, () => closeModal());

  const closeModal = () => {
    props.setRpjLocationId(null);
    formik.resetForm();
  };

  const onCommunityChange = (selected: any) => {
    formik.setFieldValue('street', []);
    formik.setFieldValue('houseNumber', []);
    formik.setFieldValue('community', selected);
  };

  const onStreetChange = (selected: any) => {
    formik.setFieldValue('houseNumber', []);
    formik.setFieldValue('street', selected);
  };

  const streetDisabled = !formik.values?.community || formik.values?.community.length === 0;
  const houseNumberDisabled = !formik.values?.street || formik.values?.street.length === 0;

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.rpjLocationId !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled adrese</ModalHeader>
        ) : (
          <ModalHeader>{props.rpjLocationId === '' ? 'Unos adrese' : 'Uređivanje adrese'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {rpjLocationDataLoading && <Loader />}
            {!rpjLocationDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Naselje'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi naselje'}
                      onItemSelect={onCommunityChange}
                      selected={formik.values?.community}
                      labelKey={'value'}
                      selectedLabelKey={'displayName'}
                      options={communitiesOptionsLoading ? [] : communitiesOptions ?? []}
                      positionFixed
                      disabled={props.readOnly}
                      error={!props.readOnly && formik.errors?.community ? (formik.errors.community as string) : ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Ulica'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi ulicu'}
                      onItemSelect={onStreetChange}
                      selected={formik.values?.street}
                      labelKey={'value'}
                      options={streetOptionsLoading ? [] : streetOptions ?? []}
                      disabled={props.readOnly || streetDisabled}
                      error={!props.readOnly && formik.errors?.street && !streetDisabled ? (formik.errors.street as string) : ''}
                      showErrorMessage
                      positionFixed
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Kućni broj'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi kućni broj'}
                      onItemSelect={selected => formik.setFieldValue('houseNumber', selected)}
                      selected={formik.values?.houseNumber}
                      labelKey={'value'}
                      options={houseNumberOptionsLoading ? [] : houseNumberOptions ?? []}
                      disabled={props.readOnly || !formik.values?.street || formik.values?.street.length === 0}
                      error={
                        !props.readOnly && formik.errors?.houseNumber && !houseNumberDisabled ? (formik.errors.houseNumber as string) : ''
                      }
                      showErrorMessage
                      positionFixed
                      clearButton
                    />
                  </Col>
                </Row>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                isLoading={isRpjLocationSaving}
                disabled={!formik.dirty || !formik.isValid}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CaseLocationRpjLocationForm;
